import { useI18n } from "@core/i18n/i18n";
import { patientManager } from "@core/service-container";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { PatientBase, PatientStatus } from "@domain/patient/patient";
import { sendAnalyticsEventPatientDeleted } from "@domain/patient/patient-analytics";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ConfirmActionDialog } from "@ui/pages/program-builder/dialog/confirm-dialog";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useState } from "react";

export interface CancelPatientInvitationDialogProps extends DialogProps {
	patient: PatientBase & { status: PatientStatus };
}

export const CancelPatientInvitationDialog = ({
	patient: { id, status, firstName, lastName },
	...otherProps
}: CancelPatientInvitationDialogProps): JSX.Element => {
	const [awaitingDelete, setAwaitingDelete] = useState(false);
	const { showToast } = useContext(ToastContext);
	const { format } = useI18n();
	const { formatPatientName } = useFormatPatients();

	const deletePatient = useCallback(async () => {
		setAwaitingDelete(true);
		try {
			await patientManager.deletePendingPatient(id);

			setAwaitingDelete(false);
			sendAnalyticsEventPatientDeleted(status);
			showToast(
				format("cancelPatientInvitationDialog.successToast.title"),
				format("cancelPatientInvitationDialog.successToast.message", {
					name: formatPatientName({ firstName, lastName }, false, false),
				}),
				"success",
			);
			otherProps.onHide();
		} catch (error) {
			console.error("Failed to cancel patient invitation", error);
			setAwaitingDelete(false);
		}
	}, [status, showToast, format, formatPatientName, firstName, lastName, otherProps, id]);

	return (
		<ConfirmActionDialog
			title={format("cancelPatientInvitationDialog.title")}
			message={format("cancelPatientInvitationDialog.message", {
				name: formatPatientName({ firstName, lastName }, false, false),
			})}
			cancelButtonLabel={format("global.patientOperation.keepInvitation")}
			onCancel={otherProps.onHide}
			confirmButtonLabel={format("global.patientOperation.cancelInvitation")}
			onConfirm={deletePatient}
			confirmLoading={awaitingDelete}
			{...otherProps}
		></ConfirmActionDialog>
	);
};
